import React, { useState, useEffect } from "react";
import { ExcursionCard } from "./ExcursionCard";
import { ExcursionListSkeleton } from "./ExcursionListSkeleton";
import { useExcursions } from '@/hooks/useExcursions';
import { useTestimonials } from '@/hooks/useTestimonials';
import { TestimonialsCarousel } from './TestimonialsCarousel';
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "./ui/carousel";
import { Mountain, Palette, TreePine, Handshake, Grid3X3, Info, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { DifficultyBadge } from "./DifficultyBadge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

const categoryIcons = {
  "aventure": Mountain,
  "culturel": Palette,
  "nature": TreePine,
  "service": Handshake
};

interface ExcursionsSectionProps {
  isFullPage?: boolean;
}

// Swipeable section component for mobile
const SwipeableExcursionSection = ({ 
  title, 
  excursions, 
  accentColor = 'bg-roca-orange' 
}: { 
  title: string; 
  excursions: any[]; 
  accentColor?: string;
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  if (excursions.length === 0) return null;

  return (
    <div className="mb-10">
      <h3 className="text-xl font-bold mb-4 capitalize">{title}</h3>
      
      {/* Mobile: carousel */}
      <div className="sm:hidden">
        <Carousel opts={{ align: "start", loop: true, slidesToScroll: 1 }} setApi={setApi} className="w-full">
          <CarouselContent className="-ml-2">
            {excursions.map((excursion) => (
              <CarouselItem key={excursion.id} className="pl-2 basis-[85%]">
                <ExcursionCard
                  id={excursion.id} title={excursion.title} category={excursion.category}
                  tags={excursion.tags} resume={excursion.resume} debut={excursion.debut}
                  duree={excursion.duree} prix_eur={excursion.prix_eur}
                  participants_min={excursion.participants_min} participants_max={excursion.participants_max}
                  niveau={excursion.niveau} cover_image={excursion.cover_image}
                  original_image={(excursion as any).original_image} image={excursion.images?.[0]}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="flex justify-center gap-1.5 mt-3">
          {excursions.map((_, index) => (
            <button key={index} onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${index === current ? `w-6 ${accentColor}` : 'w-2 bg-muted-foreground/30'}`}
              aria-label={`Excursion ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop: grid */}
      <div className="hidden sm:grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {excursions.map(excursion => (
          <ExcursionCard key={excursion.id} id={excursion.id} title={excursion.title} category={excursion.category}
            tags={excursion.tags} resume={excursion.resume} debut={excursion.debut}
            duree={excursion.duree} prix_eur={excursion.prix_eur}
            participants_min={excursion.participants_min} participants_max={excursion.participants_max}
            niveau={excursion.niveau} cover_image={excursion.cover_image}
            original_image={(excursion as any).original_image} image={excursion.images?.[0]} />
        ))}
      </div>
    </div>
  );
};

export const ExcursionsSection: React.FC<ExcursionsSectionProps> = ({
  isFullPage = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("Toutes");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const { excursions, loading } = useExcursions();
  const { testimonials } = useTestimonials();

  const getCategoryCount = (categoryName: string) => {
    return excursions.filter(e => e.category === categoryName && e.is_published).length;
  };

  const categories = [
    { name: 'aventure', color: 'text-emerald-600' },
    { name: 'culturel', color: 'text-blue-600' },
    { name: 'nature', color: 'text-green-600' },
    { name: 'service', color: 'text-orange-600' }
  ];

  const featuredExcursions = excursions
    .filter(excursion => excursion.tags?.coup_de_coeur && excursion.is_published)
    .slice(0, 3);

  // Apply category filter
  let filteredExcursions = selectedCategory === "Toutes"
    ? excursions.filter(e => e.is_published)
    : excursions.filter(e => e.category === selectedCategory && e.is_published);

  // Apply tag filter
  if (selectedTag === 'coup_de_coeur') {
    filteredExcursions = filteredExcursions.filter(e => e.tags?.coup_de_coeur);
  } else if (selectedTag === 'impact_solidaire') {
    filteredExcursions = filteredExcursions.filter(e => e.tags?.impact_solidaire);
  }

  // Group by category for section display
  const groupedByCategory = categories
    .map(cat => ({
      name: cat.name,
      excursions: filteredExcursions.filter(e => e.category === cat.name)
    }))
    .filter(g => g.excursions.length > 0);

  const difficultyLevels = [
    { level: 1, label: 'Facile', desc: 'Accessible à tous, marche légère' },
    { level: 2, label: 'Modéré', desc: 'Bonne condition physique recommandée' },
    { level: 3, label: 'Sportif', desc: 'Randonnée soutenue, dénivelé important' },
    { level: 4, label: 'Difficile', desc: 'Niveau sportif requis, terrain technique' },
    { level: 5, label: 'Expert', desc: 'Réservé aux randonneurs expérimentés' },
  ];

  if (!isFullPage) {
    return (
      <section className="py-10 sm:py-12 md:py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Découvrez nos excursions
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Des aventures authentiques pour explorer Rio sous tous ses aspects
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-10 md:mb-12">
            {Object.entries(categoryIcons).map(([category, Icon]) => (
              <div key={category} className="text-center p-6 rounded-lg bg-card border hover:shadow-md transition-all">
                <Icon className="h-12 w-12 mx-auto mb-4 text-primary" />
                <h3 className="text-lg font-semibold mb-2 capitalize">{category}</h3>
                <Badge variant="secondary" className="text-xs">Voir les excursions</Badge>
              </div>
            ))}
          </div>

          <div className="mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8 text-center">Nos excursions coup de cœur</h3>
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                <div className="col-span-full text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-roca-yellow mx-auto mb-4" />
                  <p>Chargement des excursions...</p>
                </div>
              ) : featuredExcursions.length > 0 ? (
                featuredExcursions.map(excursion => (
                  <ExcursionCard key={excursion.id} id={excursion.id} title={excursion.title} category={excursion.category} tags={excursion.tags} resume={excursion.resume} debut={excursion.debut} duree={excursion.duree} prix_eur={excursion.prix_eur} participants_min={excursion.participants_min} participants_max={excursion.participants_max} niveau={excursion.niveau} cover_image={excursion.cover_image} original_image={(excursion as any).original_image} image={excursion.images?.[0]} />
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  Aucune excursion coup de cœur disponible pour le moment.
                </div>
              )}
            </div>
          </div>

          <div className="text-center">
            <Link to="/excursions">
              <Button size="lg" className="font-semibold">Voir toutes nos excursions</Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 sm:py-12 md:py-16 bg-background">
      {loading ? <ExcursionListSkeleton /> : (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filtres catégories - scrollable on mobile */}
          <div className="mb-6">
            <div className="flex gap-3 justify-start sm:justify-center overflow-x-auto scrollbar-hide pb-2 sm:pb-0 sm:flex-wrap">
              <button 
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${selectedCategory === "Toutes" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                onClick={() => setSelectedCategory("Toutes")}
              >
                Toutes ({excursions.filter(e => e.is_published).length})
              </button>
              {categories.map(cat => {
                const Icon = categoryIcons[cat.name as keyof typeof categoryIcons];
                return (
                  <button
                    key={cat.name}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${selectedCategory === cat.name ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                    onClick={() => setSelectedCategory(cat.name)}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span className="capitalize">{cat.name}</span>
                    <span>({getCategoryCount(cat.name)})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filtres tags : coup de cœur / impact solidaire */}
          <div className="mb-8 sm:mb-10 flex flex-wrap gap-3 justify-center">
            <button
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 border ${selectedTag === 'coup_de_coeur' ? 'bg-roca-yellow/20 border-roca-yellow text-foreground' : 'border-border text-muted-foreground hover:border-roca-yellow/50'}`}
              onClick={() => setSelectedTag(selectedTag === 'coup_de_coeur' ? null : 'coup_de_coeur')}
            >
              ⭐ Coup de cœur
            </button>
            <button
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 border ${selectedTag === 'impact_solidaire' ? 'bg-green-500/20 border-green-500 text-foreground' : 'border-border text-muted-foreground hover:border-green-500/50'}`}
              onClick={() => setSelectedTag(selectedTag === 'impact_solidaire' ? null : 'impact_solidaire')}
            >
              👫 Impact solidaire
            </button>
          </div>

          {/* Légende niveaux de difficulté - collapsible on mobile */}
          <div className="mb-10 sm:mb-12">
            {/* Mobile: collapsible */}
            <div className="sm:hidden">
              <Collapsible open={difficultyOpen} onOpenChange={setDifficultyOpen}>
                <CollapsibleTrigger className="w-full flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border/50">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">Niveaux de difficulté</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${difficultyOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4 pt-3 rounded-b-xl bg-muted/50 border border-t-0 border-border/50 -mt-2">
                  <div className="grid grid-cols-1 gap-3">
                    {difficultyLevels.map(({ level, desc }) => (
                      <div key={level} className="flex items-center gap-2.5">
                        <DifficultyBadge niveau={level} size="sm" />
                        <span className="text-xs text-muted-foreground leading-tight">{desc}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
            {/* Desktop: always visible */}
            <div className="hidden sm:block p-5 rounded-xl bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">Niveaux de difficulté</span>
              </div>
              <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3">
                {difficultyLevels.map(({ level, desc }) => (
                  <div key={level} className="flex items-start gap-2.5">
                    <DifficultyBadge niveau={level} size="sm" />
                    <span className="text-xs text-muted-foreground leading-tight">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Excursions by section */}
          <div>
            {selectedCategory === "Toutes" ? (
              // Show grouped by category
              groupedByCategory.map(group => {
                const Icon = categoryIcons[group.name as keyof typeof categoryIcons] || Mountain;
                return (
                  <div key={group.name} className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <Icon className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl sm:text-3xl font-bold capitalize">
                        {group.name}
                      </h2>
                      <Badge variant="secondary">{group.excursions.length}</Badge>
                    </div>
                    <SwipeableExcursionSection
                      title=""
                      excursions={group.excursions}
                    />
                  </div>
                );
              })
            ) : (
              // Single category view
              <div>
                <div className="flex items-center justify-center gap-3 mb-8">
                  {React.createElement(categoryIcons[selectedCategory as keyof typeof categoryIcons] || Mountain, { className: "h-8 w-8 text-primary" })}
                  <h2 className="text-3xl font-bold capitalize">
                    Excursions {selectedCategory}
                  </h2>
                  <Badge variant="secondary" className="ml-2">{filteredExcursions.length}</Badge>
                </div>
                <SwipeableExcursionSection
                  title=""
                  excursions={filteredExcursions}
                />
              </div>
            )}

            {filteredExcursions.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucune excursion trouvée avec ces critères.
              </div>
            )}
          </div>

          {/* Témoignages */}
          <div className="mt-12 sm:mt-16">
            <TestimonialsCarousel testimonials={testimonials} />
          </div>
        </div>
      )}
    </section>
  );
};
